import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { priserData } from '../data';
import SEO from '../components/SEO';

const priserSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://moonyoga-fredericia.dk/priser-vilkaar/#webpage",
      "url": "https://moonyoga-fredericia.dk/priser-vilkaar",
      "name": "Priser, medlemskaber, 1:1 & vilkår | Moon Yoga Fredericia",
      "description": "Se vores priser for medlemskaber, 1:1 forløb med Jozee, lokaleudlejning samt dybdegående cupping-behandlinger og klippekort."
    },
    {
      "@type": "OfferCatalog",
      "name": "Moon Yoga Ydelser",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "1:1 med Jozee",
            "description": "Styrke, bevægelse og balance i krop og sind. Personligt yogaforløb tilpasset dine behov."
          },
          "price": "600",
          "priceCurrency": "DKK"
        }
      ]
    }
  ]
};

export default function PriserVilkaar() {
  const [openTerm, setOpenTerm] = useState(null);

  const toggleTerm = (index) => {
    setOpenTerm(openTerm === index ? null : index);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 space-y-20">
      <SEO
        title="Priser, medlemskaber & vilkår | Moon Yoga Fredericia"
        description="Se vores priser for medlemskaber, 1:1 forløb med Jozee, cupping-behandlinger, klippekort og lokaleudlejning hos Moon Yoga Fredericia."
        path="/priser-vilkaar"
        schema={priserSchema}
      />
      
      {/* Page Header */}
      <div className="text-center max-w-3xl mx-auto space-y-4">
        <span className="text-xs sm:text-sm tracking-[0.3em] uppercase text-brand-primary font-medium block">
          Vælg din vej
        </span>
        <h1 className="font-serif text-4xl sm:text-5xl text-brand-primary font-semibold tracking-wide">
          {priserData.title}
        </h1>
        <p className="text-base sm:text-lg text-brand-muted leading-relaxed">
          {priserData.intro}
        </p>
        <div className="inline-block h-1 w-16 bg-brand-primary/30 rounded-full" />
      </div>

      {/* 1. Prisoversigt med fire kolonner - Faste Medlemskaber */}
      <section className="space-y-10">
        <div className="text-center space-y-2">
          <h2 className="font-serif text-2xl sm:text-3xl text-brand-primary font-semibold tracking-wide">
            Faste Medlemskaber
          </h2>
          <p className="text-sm text-brand-muted">
            Vælg det medlemskab, der passer bedst til din ugentlige rutine.
          </p>
        </div>

        {/* 4-column desktop layout, responsive tablet/mobile */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
          {priserData.memberships.map((membership, index) => (
            <div
              key={index}
              className={`flex flex-col justify-between p-6 sm:p-8 rounded-3xl border transition-all duration-300 ${
                membership.isPlaceholder
                  ? 'bg-brand-light-gold/20 border-dashed border-brand-primary/20'
                  : 'bg-brand-surface border-brand-primary/5 hover:border-brand-primary/20 shadow-sm hover:shadow-md'
              }`}
            >
              {/* Card Top */}
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full font-semibold ${
                    membership.isPlaceholder
                      ? 'bg-brand-muted/10 text-brand-muted'
                      : 'bg-brand-primary/10 text-brand-primary'
                  }`}>
                    {membership.tag}
                  </span>
                </div>

                <div className="space-y-2">
                  <h3 className="font-serif text-2xl font-semibold text-brand-text">
                    {membership.name}
                  </h3>
                  {membership.description && (
                    <p className="text-xs text-brand-muted leading-relaxed whitespace-pre-line">
                      {membership.description}
                    </p>
                  )}
                </div>

                {/* Features List */}
                <div className="space-y-2 pt-2">
                  <span className="block text-xs font-semibold text-brand-text uppercase tracking-wider">Inkluderer:</span>
                  <ul className="space-y-3">
                    {membership.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-2.5 text-sm text-brand-text/90">
                        <i className={`fa-solid ${
                          membership.isPlaceholder ? 'fa-circle-question text-brand-muted/40' : 'fa-circle-check text-brand-primary/60'
                        } text-xs mt-1`} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Card Bottom / Prices & CTA Button */}
              <div className="mt-8 space-y-6">
                <div className="space-y-2">
                  <div className="space-y-3 py-2 border-y border-brand-primary/10">
                    <div>
                      <span className="block text-xs text-brand-muted uppercase tracking-wider">Under 30 år - Spar momsen</span>
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl sm:text-3xl font-semibold text-brand-primary">{membership.priceUnder30}</span>
                        <span className="text-sm text-brand-muted">kr./md.</span>
                      </div>
                    </div>
                    <div>
                      <span className="block text-xs text-brand-muted uppercase tracking-wider">30 år og derover</span>
                      <div className="flex items-baseline gap-1">
                        <span className="text-xl sm:text-2xl font-semibold text-brand-text">{membership.priceOver30}</span>
                        <span className="text-sm text-brand-muted">kr./md.</span>
                      </div>
                    </div>
                  </div>

                  {membership.oprettelse && (
                    <p className="text-xs text-brand-muted italic">
                      {membership.oprettelse}
                    </p>
                  )}
                </div>

                <div>
                  <a
                    href={membership.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center py-3 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-sm font-medium tracking-wide shadow-sm hover:shadow transition-all duration-300"
                  >
                    {membership.buttonText}
                  </a>
                </div>
              </div>

            </div>
          ))}
        </div>

        {/* Info Box below memberships */}
        <div className="bg-brand-light-gold/30 border border-brand-primary/10 rounded-3xl p-6 sm:p-8 max-w-4xl mx-auto space-y-4 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-brand-primary/10 rounded-2xl text-brand-primary flex-shrink-0">
              <i className="fa-solid fa-circle-info text-xl" />
            </div>
            <div className="space-y-2">
              <h3 className="font-serif text-lg font-semibold text-brand-primary">
                Vigtig information om medlemskaber
              </h3>
              <p className="text-sm text-brand-text leading-relaxed">
                Medlemskabspriserne er inklusiv moms - er du under 30 år sparer du momsen.
              </p>
              <p className="text-sm text-brand-text leading-relaxed">
                Husk at du som voksen +30 år kan gøre brug af <strong>"Motionsfradraget"</strong> på 1.750,- om året.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 2. 1:1 med Jozee */}
      <section className="max-w-4xl mx-auto bg-brand-surface border border-brand-primary/10 rounded-3xl p-6 sm:p-10 shadow-sm space-y-8">
        <div className="text-center space-y-3">
          <span className="text-xs tracking-[0.25em] uppercase text-brand-primary font-semibold block">
            Personligt Yogaforløb
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary font-semibold tracking-wide">
            {priserData.jozee1on1.title}
          </h2>
          <p className="text-lg font-serif italic text-brand-text/90">
            {priserData.jozee1on1.subtitle}
          </p>
          <div className="inline-block h-0.5 w-12 bg-brand-primary/30 rounded-full" />
        </div>

        <div className="prose prose-brand max-w-none text-brand-text/90 text-base leading-relaxed whitespace-pre-line space-y-4">
          {priserData.jozee1on1.description}
        </div>

        {/* Prices Box */}
        <div className="bg-brand-light-gold/40 border border-brand-primary/10 rounded-2xl p-6 space-y-4">
          <h3 className="font-serif text-lg font-semibold text-brand-primary">
            Priser for 1:1 forløb
          </h3>
          <ul className="space-y-3 divide-y divide-brand-primary/10">
            {priserData.jozee1on1.prices.map((item, pIdx) => (
              <li key={pIdx} className="flex justify-between items-center pt-3 first:pt-0 text-sm sm:text-base font-medium text-brand-text">
                <span>{item.option}</span>
                <span className="font-semibold text-brand-primary font-mono">{item.price}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="text-center pt-2">
          <a
            href={priserData.jozee1on1.link}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-sm font-medium tracking-wide shadow-sm hover:shadow transition-all duration-300"
          >
            <span>Book 1:1 forløb via YOGO</span>
            <i className="fa-solid fa-arrow-right text-xs" />
          </a>
        </div>
      </section>

      {/* 3. Behandlinger & Klippekort */}
      <section id="behandlinger" className="space-y-10 max-w-4xl mx-auto scroll-mt-24">
        <div className="text-center space-y-6">
          <h2 className="font-serif text-2xl sm:text-3xl text-brand-primary font-semibold tracking-wide">
            Behandlinger & Klippekort
          </h2>
          <div className="text-left space-y-6 text-sm text-brand-muted leading-relaxed">
            <div className="space-y-1.5">
              <h3 className="font-serif text-base sm:text-lg text-brand-primary font-semibold">
                Cupping behandling
              </h3>
              <p>
                Har du spændinger, smerter eller en overbelastning, som kroppen har brug for hjælp til? Behandlingen tilpasses dine behov og kombinerer scraping, dynamisk cupping og statisk cupping – alt efter hvilke områder af kroppen du ønsker at arbejde med. Cupping kan hjælpe med at løsne muskelspændinger, øge blodcirkulationen, understøtte kroppens naturlige restitution samt lindre ømhed og smerter. Mange oplever også, at behandlingen kan være en værdifuld støtte i forbindelse med overbelastninger og mindre skader. Efter behandlingen kan du nyde en økologisk kop urtete og frisk frugt i rolige omgivelser.
              </p>
            </div>
            <div className="space-y-1.5">
              <h3 className="font-serif text-base sm:text-lg text-brand-primary font-semibold">
                Roll Shaper behandling
              </h3>
              <p>
                Forkæl dig selv med en wellnessoplevelse hos Moon Yoga. Roll Shaper-behandlingen giver en dybdegående massage af musklerne og stimulerer lymfedrænagen, så kroppen føles lettere, blødere og mere afslappet. Behandlingen afsluttes med en nærende mandelolie, hvor du selv vælger din favoritduft blandt vores udvalg af æteriske olier. Efterfølgende kan du nyde en økologisk kop urtete og frisk frugt i rolige omgivelser.
              </p>
            </div>
          </div>
        </div>

        <div className="w-full">
          {priserData.otherPrices.map((cat, catIndex) => (
            <div key={catIndex} className="bg-brand-light-gold/30 border border-brand-primary/5 rounded-3xl p-6 sm:p-8 space-y-6 shadow-sm">
              <h3 className="font-serif text-xl font-semibold text-brand-primary border-b border-brand-primary/10 pb-3 text-center">
                {cat.category}
              </h3>
              <ul className="space-y-4 divide-y divide-brand-primary/10">
                {cat.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex justify-between items-start gap-4 pt-4 first:pt-0">
                    <div className="space-y-1">
                      <span className="block font-medium text-brand-text text-base sm:text-lg">{item.name}</span>
                      <span className="block text-xs sm:text-sm text-brand-muted leading-relaxed">{item.desc}</span>
                    </div>
                    <div className="text-right flex-shrink-0 pt-1">
                      <span className="font-semibold text-brand-primary text-base sm:text-lg">{item.price}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* 4. Udlejning af lokaler */}
      <section className="max-w-4xl mx-auto bg-brand-light-gold/40 border border-brand-primary/10 rounded-3xl p-6 sm:p-10 shadow-sm space-y-6">
        <div className="text-center space-y-2">
          <span className="text-xs tracking-[0.25em] uppercase text-brand-primary font-semibold block">
            Faciliteter & Rammer
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl text-brand-primary font-semibold tracking-wide">
            {priserData.lokaleudlejning.title}
          </h2>
          <div className="inline-block h-0.5 w-12 bg-brand-primary/30 rounded-full" />
        </div>

        <p className="text-base sm:text-lg text-brand-text/90 leading-relaxed whitespace-pre-line text-center">
          {priserData.lokaleudlejning.description}
        </p>

        <div className="bg-brand-surface border border-brand-primary/10 rounded-2xl p-6 text-center space-y-2">
          <span className="text-xs text-brand-muted uppercase tracking-wider block font-semibold">Pris for lokaleleje</span>
          <p className="text-xl sm:text-2xl font-serif font-semibold text-brand-primary">
            {priserData.lokaleudlejning.price}
          </p>
        </div>

        <div className="text-center space-y-4 pt-2">
          <p className="text-sm sm:text-base text-brand-text font-medium">
            {priserData.lokaleudlejning.finalText}
          </p>
          <div>
            <a
              href={`mailto:${priserData.lokaleudlejning.contactEmail}`}
              className="inline-flex items-center gap-2.5 px-8 py-3.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-sm font-medium tracking-wide shadow-sm hover:shadow transition-all duration-300"
            >
              <i className="fa-solid fa-envelope text-xs" />
              <span>Kontakt os om lokaleudlejning</span>
            </a>
          </div>
        </div>
      </section>

      {/* 5. Vilkår (Accordions) */}
      <section className="max-w-4xl mx-auto space-y-10">
        <div className="text-center space-y-2">
          <h2 className="font-serif text-2xl sm:text-3xl text-brand-primary font-semibold tracking-wide">
            Medlemsbetingelser & Vilkår
          </h2>
          <p className="text-sm text-brand-muted">
            Læs her om vores retningslinjer for booking, afmelding og opsigelse.
          </p>
        </div>

        <div className="space-y-4">
          {priserData.terms.map((term, index) => (
            <div
              key={index}
              className="border border-brand-primary/10 rounded-2xl overflow-hidden bg-brand-surface transition-all duration-300"
            >
              <button
                onClick={() => toggleTerm(index)}
                className="w-full px-6 py-4 text-left flex items-center justify-between font-serif text-lg font-semibold text-brand-text hover:text-brand-primary transition-colors duration-300"
                aria-expanded={openTerm === index}
              >
                <span>{term.title}</span>
                <i className={`fa-light ${openTerm === index ? 'fa-chevron-up' : 'fa-chevron-down'} text-brand-primary/60`} />
              </button>
              
              <div
                className={`transition-all duration-300 ease-in-out ${
                  openTerm === index ? 'max-h-[500px] opacity-100 border-t border-brand-primary/5' : 'max-h-0 opacity-0 overflow-hidden'
                }`}
              >
                <div className="p-6 bg-brand-light-gold/20 text-sm text-brand-text leading-relaxed whitespace-pre-line">
                  {term.content}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick links to full legal pages */}
        <div className="bg-brand-surface border border-brand-primary/10 rounded-2xl p-6 text-center space-y-4 shadow-sm">
          <h3 className="font-serif text-lg font-semibold text-brand-primary">
            Vil du læse de fulde betingelser?
          </h3>
          <div className="flex flex-wrap items-center justify-center gap-4 text-sm font-medium">
            <Link
              to="/tilmeldingsbetingelser"
              className="px-5 py-2.5 rounded-full bg-brand-primary/10 hover:bg-brand-primary hover:text-white text-brand-primary transition-all duration-300"
            >
              Tilmeldingsbetingelser
            </Link>
            <Link
              to="/handelsbetingelser"
              className="px-5 py-2.5 rounded-full bg-brand-primary/10 hover:bg-brand-primary hover:text-white text-brand-primary transition-all duration-300"
            >
              Handels- og forretningsbetingelser
            </Link>
            <Link
              to="/privatlivspolitik"
              className="px-5 py-2.5 rounded-full bg-brand-primary/10 hover:bg-brand-primary hover:text-white text-brand-primary transition-all duration-300"
            >
              Privatlivspolitik
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
